// The pure rules behind "Did you go? How was it?" and the feedback pad (R-14 app half, build/handoff/TASTE-FEEDBACK.md).
// No imports on purpose: tests/taste_feedback_test.ts runs this file under Deno.
//
// Alvin 10/08: show it the NEXT TIME THE PERSON OPENS THE APP after any outing whose date has passed (saved / planned /
// calendar-added items with a past time); prompts accrue into a small queue shown one at a time on one card; never more than
// one prompt surface per app open. Yes -> the pad. No -> didnt_go (not a dislike). Skip -> asked again on a later open,
// at most twice.

export type OutingSource = 'calendar' | 'saved' | 'plan'
export type OutingItem = { id: string; title: string; kind: string; start_at: string | null; end_at: string | null }
export type Outing<I extends OutingItem = OutingItem> = { item: I; /** ms: when it was over */ at: number; source: OutingSource }

/** An outing older than this is not asked about (a person does not remember a Tuesday from two months ago). */
export const WINDOW_DAYS = 30
/** Skipped this many times: never asked again. */
export const MAX_SKIPS = 2
/** An event with no end counts as over this long after its start (browse's own rule: _shared notOver). */
export const NO_END_MS = 3 * 3600e3
/** A date-only start ("2026-10-11T00:00" in LA, or a UTC midnight) means the whole day. */
const DAY_MS = 24 * 3600e3

/** When a dated item was over, in ms; null for a place with no date (a saved museum has no "date that passed"). */
export function overAt(item: Pick<OutingItem, 'kind' | 'start_at' | 'end_at'>): number | null {
  if (item.end_at) {
    const e = Date.parse(item.end_at)
    if (Number.isFinite(e)) return e
  }
  if (item.kind !== 'event' || !item.start_at) return null
  const s = Date.parse(item.start_at)
  if (!Number.isFinite(s)) return null
  return s + NO_END_MS
}

/** A plan slot's own time (an itinerary stop), falling back to the item's. */
export function slotOverAt(slot: { start: string | null; end: string | null }, item: Pick<OutingItem, 'kind' | 'start_at' | 'end_at'> | null): number | null {
  if (slot.end) { const e = Date.parse(slot.end); if (Number.isFinite(e)) return e }
  if (slot.start) { const s = Date.parse(slot.start); if (Number.isFinite(s)) return s + NO_END_MS }
  return item ? overAt(item) : null
}

/**
 * The check-in queue: every outing that is over (and not older than WINDOW_DAYS), once per item (its latest time wins),
 * minus what they already answered (went, didnt_go, pad, the older how-was-it, or a not for me) and what they skipped
 * MAX_SKIPS times. Most recent first: the freshest memory is the best answer.
 */
export function buildQueue<I extends OutingItem>(outings: Outing<I>[], answered: Set<string>, skips: Record<string, number>, now: number): Outing<I>[] {
  const byItem = new Map<string, Outing<I>>()
  for (const o of outings) {
    if (!o.item?.id || !Number.isFinite(o.at)) continue
    if (o.at > now || o.at < now - WINDOW_DAYS * DAY_MS) continue
    if (answered.has(o.item.id) || (skips[o.item.id] ?? 0) >= MAX_SKIPS) continue
    const had = byItem.get(o.item.id)
    if (!had || o.at > had.at) byItem.set(o.item.id, o)
  }
  return [...byItem.values()].sort((a, b) => b.at - a.at)
}

/** One more skip for this item (returns a new record). */
export const addSkip = (skips: Record<string, number>, id: string): Record<string, number> => ({ ...skips, [id]: (skips[id] ?? 0) + 1 })

// ---------------------------------------------------------------- the pad
export const PAD_AXES = ['connection', 'again', 'worth', 'learned', 'challenge'] as const
export type PadAxis = (typeof PAD_AXES)[number]
/** The server's dead zone (supabase/functions/_shared/taste_weights.ts PAD_DEAD_ZONE). */
export const PAD_DEAD = 15

/** A tap at (px, py) inside a w x h square -> the pad's x, y: whole numbers from -100 (left / bottom) to 100 (right / top). */
export function padPoint(px: number, py: number, w: number, h: number): { x: number; y: number } {
  const clamp = (v: number) => Math.max(-100, Math.min(100, Math.round(v)))
  return { x: clamp((px / Math.max(1, w)) * 200 - 100), y: clamp(100 - (py / Math.max(1, h)) * 200) }
}

/** Which word each side of the readback uses: 'unsure' = the middle of both (the dead zone). */
export function padBands(x: number, y: number): { x: 'low' | 'mid' | 'high'; y: 'low' | 'mid' | 'high' } | 'unsure' {
  if (Math.abs(x) < PAD_DEAD && Math.abs(y) < PAD_DEAD) return 'unsure'
  const band = (v: number) => (v >= PAD_DEAD ? 'high' : v <= -PAD_DEAD ? 'low' : 'mid') as 'low' | 'mid' | 'high'
  return { x: band(x), y: band(y) }
}

/** The server's reason keys, in the order the reasons are offered (logistics first, then taste). */
export const NFM_REASONS = ['too_far', 'too_late', 'too_pricey', 'wrong_day', 'been_there', 'sold_out', 'no_time', 'too_crowded',
  'wrong_vibe', 'not_my_thing', 'too_touristy', 'seen_enough_of_this'] as const
export type NfmReason = (typeof NFM_REASONS)[number]
